// Helpers para el manejo de bonos (aguinaldos, etc.) cargados masivamente
// sobre la Liquidación de Honorarios (adelanto / fin de mes).

export const normalizeRut = (rut) =>
    (rut || "").toString().toUpperCase().replace(/[.\-\s]/g, "");

/**
 * Combina las cabeceras de bono (con su detalle por auxiliar) con los resultados de la
 * tabla de honorarios del periodo, agregando por auxiliar:
 *  - bono_bruto_total: suma de todos los bonos del periodo/tipo
 *  - bonos_por_glosa: { [glosa]: montoBruto } para columnas dinámicas y resumen
 *
 * Un auxiliar puede tener un bono cargado sin tener pago (turnos) ese periodo — en ese
 * caso igual debe aparecer en la tabla. `auxiliaresActivos` (roster completo de auxiliares
 * activos, con anticipo/teléfono/etc.) se usa para completar esa fila sintética con sus
 * datos reales; si no se encuentra ahí, se usa el rut/nombre guardado en el propio bono.
 */
export const mergeBonosEnResultados = (resultados, bonoCabeceras, auxiliaresActivos = []) => {
    const existingIds = new Set(resultados.map(r => Number(r.auxiliar_id)));
    // Fallback por RUT: si existen auxiliares duplicados en la base (mismo RUT, distinto id),
    // el bono puede quedar matcheado a un auxiliar_id distinto al que ya tiene fila por turnos.
    // Comparar también por RUT evita que esa persona aparezca dos veces ("Solo Bono").
    const existingRuts = new Set(
        resultados.map(r => normalizeRut(r.rut)).filter(Boolean)
    );
    const auxiliaresPorId = new Map(
        (auxiliaresActivos || []).map(a => [Number(a.auxiliar_id), a])
    );

    const filas = [...resultados];
    const idsAgregados = new Set();

    (bonoCabeceras || []).forEach(cab => {
        (cab.detalle || []).forEach(item => {
            const id = Number(item.auxiliar_id);
            const rutNorm = normalizeRut(item.rut);
            if (existingIds.has(id) || idsAgregados.has(id)) return;
            if (rutNorm && existingRuts.has(rutNorm)) return;
            idsAgregados.add(id);
            if (rutNorm) existingRuts.add(rutNorm);

            const auxiliar = auxiliaresPorId.get(id);
            filas.push(
                auxiliar
                    ? { ...auxiliar, sin_pago_periodo: true }
                    : {
                        auxiliar_id: id,
                        rut: item.rut,
                        nombre_auxiliar: item.nombre_auxiliar || "",
                        apepat_auxiliar: "",
                        apemat_auxiliar: "",
                        sin_pago_periodo: true,
                    }
            );
        });
    });

    return filas.map(row => {
        let brutoTotal = 0;
        const porGlosa = {};
        const rowRut = normalizeRut(row.rut);

        (bonoCabeceras || []).forEach(cab => {
            const item = (cab.detalle || []).find(
                d => Number(d.auxiliar_id) === Number(row.auxiliar_id)
                    || (rowRut && normalizeRut(d.rut) === rowRut)
            );
            if (item) {
                const monto = Number(item.monto_bruto) || 0;
                brutoTotal += monto;
                porGlosa[cab.glosa] = (porGlosa[cab.glosa] || 0) + monto;
            }
        });

        return { ...row, bono_bruto_total: brutoTotal, bonos_por_glosa: porGlosa };
    });
};

/** Lista de glosas distintas presentes en las cabeceras de bono del periodo/tipo. */
export const glosasDeBonos = (bonoCabeceras) => {
    const set = new Set();
    (bonoCabeceras || []).forEach(cab => set.add(cab.glosa));
    return Array.from(set);
};
